import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Trees, Plus, Edit, Trash2, LogOut, Music, Loader2, MapPin, Upload, Mic } from 'lucide-react';
import AddTreeModal from '../components/AddTreeModal';
import EditTreeModal from '../components/EditTreeModal';
import AddAudioModal from '../components/AddAudioModal';
import BulkUploadModal from '../components/BulkUploadModal';
import AudioRecorderModal from '../components/AudioRecorderModal';
import ManageAudioModal from '../components/ManageAudioModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminDashboard = () => {
  const [trees, setTrees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showAudioModal, setShowAudioModal] = useState(false);
  const [showBulkUploadModal, setShowBulkUploadModal] = useState(false);
  const [showAudioRecorderModal, setShowAudioRecorderModal] = useState(false);
  const [showManageAudioModal, setShowManageAudioModal] = useState(false);
  const [selectedTree, setSelectedTree] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/admin/login');
      return;
    }
    fetchTrees();
  }, [navigate]);

  const fetchTrees = async () => {
    try {
      const response = await axios.get(`${API}/trees`);
      setTrees(response.data);
    } catch (error) {
      console.error('Error fetching trees:', error);
      toast.error('Failed to fetch trees');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    toast.success('Logged out successfully');
    navigate('/admin/login');
  };

  const handleDelete = async (treeId) => {
    if (!window.confirm('Are you sure you want to delete this tree?')) {
      return;
    }

    try {
      const token = localStorage.getItem('admin_token');
      await axios.delete(`${API}/trees/${treeId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Tree deleted successfully');
      fetchTrees();
    } catch (error) {
      console.error('Error deleting tree:', error);
      toast.error('Failed to delete tree');
    }
  };

  const handleEdit = (tree) => {
    setSelectedTree(tree);
    setShowEditModal(true);
  };

  const handleAddAudio = (tree) => {
    setSelectedTree(tree);
    setShowAudioModal(true);
  };

  const handleRecordAudio = (tree) => {
    setSelectedTree(tree);
    setShowAudioRecorderModal(true);
  };

  const handleManageAudio = (tree) => {
    setSelectedTree(tree);
    setShowManageAudioModal(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen" data-testid="admin-dashboard-loading">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-secondary" data-testid="admin-dashboard-page">
      {/* Header */}
      <header className="bg-white border-b border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-2">
              <Trees className="w-6 h-6 text-primary" />
              <span className="text-xl font-heading font-bold text-primary">TreeMuseum Admin</span>
            </div>
            <div className="flex items-center gap-4">
              <Link
                to="/admin/settings"
                className="text-sm font-medium text-muted-foreground hover:text-primary transition-colors"
                data-testid="settings-link"
              >
                Settings
              </Link>
              <Link
                to="/admin/s3-files"
                className="text-sm font-medium text-muted-foreground hover:text-primary transition-colors"
                data-testid="s3-files-link"
              >
                S3 Files
              </Link>
              <Link
                to="/"
                className="text-sm font-medium text-muted-foreground hover:text-primary transition-colors"
                data-testid="view-site-link"
              >
                View Site
              </Link>
              <button
                onClick={handleLogout}
                className="flex items-center gap-2 text-sm font-medium text-muted-foreground hover:text-destructive transition-colors"
                data-testid="logout-button"
              >
                <LogOut className="w-4 h-4" />
                Logout
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats & Actions */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-heading font-bold text-primary mb-2" data-testid="dashboard-title">
              Manage Trees
            </h1>
            <p className="text-sm text-muted-foreground">
              {trees.length} {trees.length === 1 ? 'tree' : 'trees'} in collection
            </p>
          </div>
          <div className="flex flex-wrap gap-3">
            <button
              onClick={() => setShowBulkUploadModal(true)}
              className="flex items-center gap-2 px-5 py-2.5 bg-white border border-border text-foreground rounded-lg font-medium hover:bg-secondary transition-all active:scale-95 shadow-sm"
              data-testid="bulk-upload-button"
            >
              <Upload className="w-4 h-4" />
              Bulk Upload
            </button>
            <button
              onClick={() => setShowAddModal(true)}
              className="flex items-center gap-2 px-6 py-2.5 bg-primary text-white rounded-lg font-medium hover:bg-primary/90 transition-all active:scale-95 shadow-md"
              data-testid="add-tree-button"
            >
              <Plus className="w-5 h-5" />
              Add Tree
            </button>
          </div>
        </div>

        {/* Trees Table */}
        <div className="bg-white border border-border rounded-lg shadow-sm overflow-hidden" data-testid="trees-table">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-secondary/50 border-b border-border">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Tree
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Scientific Name
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Location
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Audio Tracks
                  </th>
                  <th className="px-6 py-4 text-right text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {trees.map((tree) => (
                  <tr key={tree.id} className="hover:bg-secondary/30 transition-colors" data-testid={`tree-row-${tree.id}`}>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <img
                          src={tree.image_url}
                          alt={tree.name}
                          className="w-12 h-12 rounded object-cover"
                        />
                        <div>
                          <div className="font-medium text-primary">{tree.name}</div>
                          <div className="text-xs text-muted-foreground">{tree.family}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-sm italic text-muted-foreground">{tree.scientific_name}</span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-1 text-xs text-muted-foreground font-mono">
                        <MapPin className="w-3 h-3" />
                        <span>{tree.location.lat.toFixed(2)}, {tree.location.lng.toFixed(2)}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleManageAudio(tree)}
                          className="flex items-center gap-1 px-2 py-1 text-sm text-primary hover:bg-primary/10 rounded transition-colors"
                          title="Manage audio tracks"
                          data-testid={`manage-audio-${tree.id}`}
                        >
                          <span className="font-medium">{tree.audio_tracks?.length || 0}</span>
                          <Music className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleRecordAudio(tree)}
                          className="p-1 text-destructive hover:bg-destructive/10 rounded transition-colors"
                          title="Record audio"
                          data-testid={`record-audio-${tree.id}`}
                        >
                          <Mic className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleAddAudio(tree)}
                          className="p-1 text-accent hover:bg-accent/10 rounded transition-colors"
                          title="Upload audio URL"
                          data-testid={`add-audio-${tree.id}`}
                        >
                          <Plus className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => handleEdit(tree)}
                          className="p-2 text-primary hover:bg-primary/10 rounded transition-colors"
                          title="Edit tree"
                          data-testid={`edit-tree-${tree.id}`}
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(tree.id)}
                          className="p-2 text-destructive hover:bg-destructive/10 rounded transition-colors"
                          title="Delete tree"
                          data-testid={`delete-tree-${tree.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {trees.length === 0 && (
            <div className="text-center py-12" data-testid="no-trees-message">
              <Trees className="w-12 h-12 text-muted-foreground mx-auto mb-4 opacity-30" />
              <p className="text-muted-foreground mb-4">No trees yet. Add your first tree!</p>
              <button
                onClick={() => setShowAddModal(true)}
                className="inline-flex items-center gap-2 px-6 py-2 bg-primary text-white rounded-lg font-medium hover:bg-primary/90 transition-all"
              >
                <Plus className="w-4 h-4" />
                Add Tree
              </button>
            </div>
          )}
        </div>
      </main>

      {/* Modals */}
      {showAddModal && (
        <AddTreeModal
          onClose={() => setShowAddModal(false)}
          onSuccess={() => {
            setShowAddModal(false);
            fetchTrees();
          }}
        />
      )}

      {showEditModal && selectedTree && (
        <EditTreeModal
          tree={selectedTree}
          onClose={() => {
            setShowEditModal(false);
            setSelectedTree(null);
          }}
          onSuccess={() => {
            setShowEditModal(false);
            setSelectedTree(null);
            fetchTrees();
          }}
        />
      )}

      {showAudioModal && selectedTree && (
        <AddAudioModal
          tree={selectedTree}
          onClose={() => {
            setShowAudioModal(false);
            setSelectedTree(null);
          }}
          onSuccess={() => {
            setShowAudioModal(false);
            setSelectedTree(null);
            fetchTrees();
          }}
        />
      )}

      {showBulkUploadModal && (
        <BulkUploadModal
          onClose={() => setShowBulkUploadModal(false)}
          onSuccess={() => {
            setShowBulkUploadModal(false);
            fetchTrees();
          }}
        />
      )}

      {showAudioRecorderModal && selectedTree && (
        <AudioRecorderModal
          tree={selectedTree}
          onClose={() => {
            setShowAudioRecorderModal(false);
            setSelectedTree(null);
          }}
          onSuccess={() => {
            setShowAudioRecorderModal(false);
            setSelectedTree(null);
            fetchTrees();
          }}
        />
      )}

      {showManageAudioModal && selectedTree && (
        <ManageAudioModal
          tree={selectedTree}
          onClose={() => {
            setShowManageAudioModal(false);
            setSelectedTree(null);
          }}
          onSuccess={() => {
            setShowManageAudioModal(false);
            setSelectedTree(null);
            fetchTrees();
          }}
        />
      )}
    </div>
  );
};

export default AdminDashboard;